// @flow
import React, { useContext, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Formik, type FormikHelpers } from 'formik';

import { t } from 'Src/i18n';
import ReactAppContext from 'Context/ReactAppContext';
import { useRouter } from 'Client/router/useRouter';
import { fetchPrebookingTravelers } from 'Store/prebooking-form/prebookingform.action';

import {
  getFormSchema,
  getInitialFormValues,
  handleSubmit,
  getFormTravelerAndCompanions,
  getFormUser,
  getFormOptins,
  type FormValues,
  type InitialFormData,
} from './PrebookingForm.utils';
import { PrebookingForm, type PrebookingFormBaseProps } from './PrebookingForm';

type State = {
  userInfo: UserInfo,
  seller: {
    neolids: Array<string>,
  },
  tripInformation: {
    adults: Array<Adult>,
    kids: Array<Kid>,
  },
};

type PrebookingFormState = {
  initialFormData: InitialFormData,
  travelers: null | {
    adults: Array<string>,
    kids: Array<string>,
  },
};

type PrebookingFormContainerProps = {
  ...$Exact<PrebookingFormBaseProps>,
  cxoAgency: null | string,
  cxoId: null | string,
  redirectUris: {
    error: null | string,
    success: null | string,
  },
};

export function PrebookingFormContainer({
  adultsOptions,
  agencies,
  civilities,
  cxoAgency,
  cxoId,
  dateFormat,
  departureDateEnd,
  departureDateStart,
  hasSeller,
  hasUnicodeFields,
  hasUser,
  kidsOptions,
  newsletterOptins,
  productOptions,
  products,
  redirectUris,
}: PrebookingFormContainerProps) {
  const appContext = useContext(ReactAppContext);
  const { history, location } = useRouter<{}, {}>();

  const redirects = {
    errorUrl: redirectUris.error || '/',
    successUrl: redirectUris.success || '/',
  };
  const productIds = productOptions.map(productOption => productOption.value).filter(Boolean);

  const dispatch = useDispatch();
  const { initialFormData, travelers } = useSelector(
    (state: State): PrebookingFormState => {
      const {
        userInfo,
        seller = { neolids: [] },
        tripInformation: { adults, kids } = { adults: [], kids: [] },
      } = state;

      if (seller.neolids.length > 0) {
        return {
          initialFormData: getFormTravelerAndCompanions({ adults, kids }),
          travelers: {
            adults: adults.map(adult => adult.neolid).filter(Boolean),
            kids: kids.map(kid => kid.neolid).filter(Boolean),
          },
        };
      }

      return {
        initialFormData: { traveler: getFormUser(userInfo), ...getFormOptins(userInfo) },
        travelers: null,
      };
    },
  );

  const travelerIds = travelers ? [...travelers.adults, ...travelers.kids].join() : '';

  // CXO: fetch complete traveler information
  useEffect(
    () => {
      if (hasSeller && travelerIds.length > 0) {
        dispatch(fetchPrebookingTravelers(travelers, appContext));
      }
    },
    [travelerIds],
  );

  return (
    <Formik
      initialValues={getInitialFormValues(cxoAgency)}
      validationSchema={getFormSchema(t, {
        agencies: agencies.map(agency => agency.value),
        civilities,
        locale: appContext.locale,
        productIds,
      })}
      onSubmit={async (values: FormValues, actions: FormikHelpers) => {
        await handleSubmit(values, {
          actions,
          appContext,
          cxoId,
          dateFormat,
          history,
          location,
          redirects,
        });
      }}
    >
      <PrebookingForm
        adultsOptions={adultsOptions}
        agencies={agencies}
        civilities={civilities}
        dateFormat={dateFormat}
        departureDateEnd={departureDateEnd}
        departureDateStart={departureDateStart}
        hasUnicodeFields={hasUnicodeFields}
        initialFormData={initialFormData}
        hasUser={hasUser}
        hasSeller={hasSeller}
        kidsOptions={kidsOptions}
        language={appContext.language}
        newsletterOptins={newsletterOptins}
        productOptions={productOptions}
        products={products}
      />
    </Formik>
  );
}
